/** MediRecords activeStatus: 1=Active, 2=Inactive, 3=Deleted */
export type MediRecordsPatientRecordStatus = 1 | 2 | 3;

export const MEDIRECORDS_PATIENT_RECORD_STATUS = {
  1: 'Active',
  2: 'Inactive',
  3: 'Deleted',
} as const;

/** MediRecords patientStatusCode: 1=Standard, 2=VIP, 3=Family, 4=Blacklisted */
export type MediRecordsPatientStatusCode = 1 | 2 | 3 | 4;

export const MEDIRECORDS_PATIENT_STATUS = {
  1: 'Standard',
  2: 'VIP',
  3: 'Family',
  4: 'Blacklisted',
} as const;

/**
 * Toggle flags for which fields to include in MediRecords patient search.
 * Maps to MediRecordsSearchParams fields in the API.
 * Note: medicare toggle controls both medicareNo and medicareIRN fields.
 */
export type MediRecordsSearchFieldToggles = {
  firstName: boolean;
  lastName: boolean;
  dob: boolean;
  mobilePhone: boolean;
  postcode: boolean;
  /** Controls both medicareNo and medicareIRN in the search query */
  medicare: boolean;
};

/**
 * Default search field toggles - all fields enabled by default.
 * Used as initial state in frontend and fallback in backend.
 */
export const DEFAULT_SEARCH_FIELD_TOGGLES: MediRecordsSearchFieldToggles = {
  firstName: true,
  lastName: true,
  dob: true,
  mobilePhone: true,
  postcode: true,
  medicare: true,
} as const;

export const ADDRESS_SYNC_TRIGGER = {
  ADDRESS_UPDATE: 'ADDRESS_UPDATE',
  STAFF_VISIT: 'STAFF_VISIT',
  PRESCRIBED_IN_CANVIEW: 'PRESCRIBED_IN_CANVIEW',
  CHECKUP_COMPLETE: 'CHECKUP_COMPLETE',
  CANVIEW_REDIRECT: 'CANVIEW_REDIRECT',
  MANUAL: 'MANUAL',
} as const;

export type AddressSyncTrigger = (typeof ADDRESS_SYNC_TRIGGER)[keyof typeof ADDRESS_SYNC_TRIGGER];

export type MediRecordsAddressSyncStatus = {
  lastAttempt: number;
  status: 'SUCCESS' | 'FAILED';
  error?: string;
  trigger: AddressSyncTrigger;
};

export const MEDIRECORDS_SYNC_STATUSES = {
  STARTED: 'STARTED',
  NO_RESULT_NOT_SYNCED: 'NO_RESULT_NOT_SYNCED',
  NO_RESULT_BUT_SYNCED: 'NO_RESULT_BUT_SYNCED',
  MULTIPLE_RESULTS: 'MULTIPLE_RESULTS',
  INVALID_RESULT: 'INVALID_RESULT',
  ERROR: 'ERROR',
  SINGLE_RESULT_NOT_SYNCED: 'SINGLE_RESULT_NOT_SYNCED',
  SINGLE_RESULT_SYNCED: 'SINGLE_RESULT_SYNCED',
  NOT_STARTED: 'NOT_STARTED',
  NO_ACTIVE_SCRIPTS: 'NO_ACTIVE_SCRIPTS',
  ACCOUNT_DISABLED_OR_LOCKED: 'ACCOUNT_DISABLED_OR_LOCKED',
  FAILED_TO_UPLOAD: 'FAILED_TO_UPLOAD',
} as const;

export type MediRecordsSyncStatus = (typeof MEDIRECORDS_SYNC_STATUSES)[keyof typeof MEDIRECORDS_SYNC_STATUSES];

export const eligibleStatusesForBatchSync: MediRecordsSyncStatus[] = [
  MEDIRECORDS_SYNC_STATUSES.SINGLE_RESULT_NOT_SYNCED,
  MEDIRECORDS_SYNC_STATUSES.NO_RESULT_NOT_SYNCED,
];

/**
 * THE FOLLOWING TYPES WERE EXTRACTED FROM THE MEDIRECORDS API BACKEND (see functions/src/medirecords/api)
 */

/** Query result from MediRecords - individual patient record */
export type QueryPatientResult = {
  type: 'patient';
  id: string;
  firstName: string | null;
  lastName: string;
  preferredName?: string | null;
  dob: string;
  email?: string | null;
  mobilePhone?: string | null;
  patientSequenceNumber?: number;
  createdDateTime?: string;
  /** 1=Standard, 2=VIP, 3=Family, 4=Blacklisted */
  patientStatusCode?: number | null;
  patientStatus?: string;
  _activeStatusQueried: MediRecordsPatientRecordStatus;
  _activeStatusLabel: string;
};

/** Error result for a specific search status */
export type QueryErrorResult = {
  type: 'error';
  status?: MediRecordsPatientRecordStatus;
  statusLabel: string;
  message: string;
};

/** Discriminated union of possible results */
export type QueryResult = QueryPatientResult | QueryErrorResult;

/** Search criteria shown to staff so they know what was queried */
export type SearchCriteria = {
  firstName?: string;
  lastName?: string;
  dob?: string;
  mobilePhone?: string;
  postcode?: string;
  medicareNo?: string;
  medicareIRN?: string;
};

/** Response from querying patient in MediRecords */
export type QueryPatientResponse = {
  results: QueryResult[];
  searchCriteria: SearchCriteria;
};

/** Synced MediRecords search results stored in patient document */
export type SyncedMediRecordsSearchResults = QueryPatientResponse & {
  updatedAt: unknown; // Firestore Timestamp (different types between frontend/backend)
  status: MediRecordsSyncStatus;
};

/** MediRecords sync configuration stored in Firestore */
export type MediRecordsSyncConfig = {
  scanBatchSize: number;
  scanConcurrency: number;
  uploadConcurrency: number;
};

export const DEFAULT_MEDIRECORDS_SYNC_CONFIG: MediRecordsSyncConfig = {
  scanBatchSize: 300,
  scanConcurrency: 5,
  uploadConcurrency: 3,
};
